import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import i18n from '../i18n/config';
import API_ENDPOINTS from '../config';
import { authenticatedFetchJson } from '../utils/api';
import logger from '../utils/logger';

export default function OrganizationAudit() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [filters, setFilters] = useState({
    action: '',
    startDate: '',
    endDate: '',
  });

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      // Org admin automatically filtered to their org by backend
      if (filters.action) {
        params.append('action', filters.action);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const data = await authenticatedFetchJson(`${API_ENDPOINTS.API_BASE_URL}/api/audit/logs?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (error) {
      logger.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [offset, limit, filters]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setOffset(0);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.action) {
        params.append('action', filters.action);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const { authenticatedFetchBlob } = await import('../utils/api');
      const blob = await authenticatedFetchBlob(`${API_ENDPOINTS.API_BASE_URL}/api/audit/export?${params}`);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      logger.error('Error exporting audit logs:', error);
      if (error.message !== 'Unauthorized - redirected to login') {
        alert(t('organizationAudit.exportError'));
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const localeMap = {
        'sr': 'sr-RS',
        'zh': 'zh-CN',
        'en': 'en-US'
      };
      const locale = localeMap[i18n.language] || 'en-US';
      return date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatAction = (action) => {
    const actionMap = {
      'member_invited': t('organizationAudit.actionMemberInvited'),
      'invitation_accepted': t('organizationAudit.actionInvitationAccepted'),
      'member_removed': t('organizationAudit.actionMemberRemoved'),
      'role_changed': t('organizationAudit.actionRoleChanged'),
    };
    return actionMap[action] || action;
  };

  const formatUser = (user) => {
    if (!user) return '-';
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
    }
    return user.email || '-';
  };

  const actionTypes = [
    { value: '', label: t('organizationAudit.allActions') },
    { value: 'member_invited', label: t('organizationAudit.actionMemberInvited') },
    { value: 'invitation_accepted', label: t('organizationAudit.actionInvitationAccepted') },
    { value: 'member_removed', label: t('organizationAudit.actionMemberRemoved') },
    { value: 'role_changed', label: t('organizationAudit.actionRoleChanged') },
  ];

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem', fontWeight: 700, color: colors.text, transition: 'color 0.3s ease' }}>
          {t('organizationAudit.title')}
        </h1>
        <p style={{ margin: 0, color: colors.textSecondary, transition: 'color 0.3s ease' }}>
          {t('organizationAudit.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div style={{
        background: colors.card,
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: `0 1px 3px ${colors.shadow}`,
        marginBottom: '1.5rem',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>
              {t('organizationAudit.action')}
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: colors.background,
                color: colors.text,
                transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
              }}
            >
              {actionTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>
              {t('organizationAudit.fromDate')}
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: colors.background,
                color: colors.text,
                transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>
              {t('organizationAudit.toDate')}
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: colors.background,
                color: colors.text,
                transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              setFilters({
                action: '',
                startDate: '',
                endDate: '',
              });
              setOffset(0);
            }}
            style={{
              padding: '8px 16px',
              background: colors.buttonSecondaryBg,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: colors.buttonSecondaryText,
              transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = colors.buttonSecondaryHoverBg}
            onMouseLeave={(e) => e.target.style.backgroundColor = colors.buttonSecondaryBg}
          >
            {t('organizationAudit.resetFilters')}
          </button>
          <button
            onClick={handleExport}
            style={{
              padding: '8px 16px',
              background: colors.primary,
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: 'white',
              fontWeight: 600,
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.background = colors.primaryHover}
            onMouseLeave={(e) => e.target.style.background = colors.primary}
          >
            {t('organizationAudit.exportCSV')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: colors.card,
        borderRadius: '8px',
        boxShadow: `0 1px 3px ${colors.shadow}`,
        overflow: 'hidden',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
      }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: colors.textSecondary, transition: 'color 0.3s ease' }}>
            {t('organizationAudit.loading')}
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: colors.textSecondary, transition: 'color 0.3s ease' }}>
            {t('organizationAudit.noLogs')}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundLight, borderBottom: `2px solid ${colors.border}`, transition: 'background-color 0.3s ease, border-color 0.3s ease' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>{t('organizationAudit.dateTime')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>{t('organizationAudit.action')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>{t('organizationAudit.executedBy')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>{t('organizationAudit.targetUser')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text, transition: 'color 0.3s ease' }}>{t('organizationAudit.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} style={{ borderBottom: `1px solid ${colors.border}`, transition: 'border-color 0.3s ease' }}>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text, transition: 'color 0.3s ease' }}>
                        {formatDate(log.createdAt)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text, transition: 'color 0.3s ease' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: log.action === 'member_removed' 
                            ? (isDark ? '#7f1d1d' : '#fee2e2') 
                            : log.action === 'role_changed' 
                            ? (isDark ? '#78350f' : '#fef3c7') 
                            : (isDark ? '#1e3a8a' : '#dbeafe'),
                          color: log.action === 'member_removed' 
                            ? (isDark ? '#fca5a5' : '#dc2626') 
                            : log.action === 'role_changed' 
                            ? (isDark ? '#fbbf24' : '#d97706') 
                            : (isDark ? '#93c5fd' : '#2563eb'),
                          transition: 'background-color 0.2s, color 0.2s',
                        }}>
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text, transition: 'color 0.3s ease' }}>
                        {formatUser(log.user)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text, transition: 'color 0.3s ease' }}>
                        {formatUser(log.targetUser)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: colors.text, transition: 'color 0.3s ease' }}>
                        {log.metadata ? (
                          <details>
                            <summary style={{ cursor: 'pointer', color: colors.primary, transition: 'color 0.2s' }}>{t('organizationAudit.show')}</summary>
                            <pre style={{
                              marginTop: '8px',
                              padding: '8px',
                              background: colors.backgroundLight,
                              borderRadius: '4px',
                              fontSize: '12px',
                              overflow: 'auto',
                              color: colors.text,
                              transition: 'background-color 0.3s ease, color 0.3s ease',
                            }}>
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          </details>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              padding: '1rem',
              borderTop: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'border-color 0.3s ease',
            }}>
              <div style={{ fontSize: '14px', color: colors.textSecondary, transition: 'color 0.3s ease' }}>
                {t('organizationAudit.showingLogs', { from: offset + 1, to: Math.min(offset + limit, total), total })}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  style={{
                    padding: '6px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    background: offset === 0 ? colors.backgroundHover : colors.background,
                    cursor: offset === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: offset === 0 ? colors.textTertiary : colors.text,
                    transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
                  }}
                >
                  {t('organizationAudit.previous')}
                </button>
                <span style={{ padding: '6px 12px', fontSize: '14px', color: colors.text, transition: 'color 0.3s ease' }}>
                  {t('organizationAudit.pageOf', { page: currentPage, pages: totalPages })}
                </span>
                <button
                  onClick={() => setOffset(Math.min((totalPages - 1) * limit, offset + limit))}
                  disabled={offset + limit >= total}
                  style={{
                    padding: '6px 12px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    background: offset + limit >= total ? colors.backgroundHover : colors.background,
                    cursor: offset + limit >= total ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: offset + limit >= total ? colors.textTertiary : colors.text,
                    transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
                  }}
                >
                  {t('organizationAudit.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
